/**
 * @author: Wangwei
 * @description: 打卡营学习预览模式
 */
import { useRequest, history } from 'umi';
import styles from '@/pages/camp/css/studyIndexCSS.less';
import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { Form, Button, Rate, Anchor, Skeleton, Modal, Tooltip } from 'antd';
import { getNodeContent, getExercisesResults } from '@/utils/requests/camp/requests_study';
import { NodeContentType, ExercisesResultsType, NodeStudyState } from '@/types/camp/study/index';
import {
  NodeContentTypes,
  ContentTypes,
  TextType,
  VideoType,
  CertificationType,
  VideoLogParamsType,
} from '@/types/camp/study/contentTypes';
import Videos from './video';
import Cert from './cert';
import QuestionPreview from './questionPreview';
import Editor from '@digit-fe/digit-editor-new';
import { editParseJson } from '@/utils/index';
import { StudyInfoType } from '@/types/camp/study/studentBaseInfoType';
import { ProgressType, StudyNode } from '@/types/camp/details/detailsType';
import { NodeReviewState } from '@/types/camp/study/index';
import successSvg from '@/static/images/success.svg';
import { ReactComponent as RewardSvg } from '@/static/images/reward2.svg';
import { useLocalStorageState } from 'ahooks';

interface Props {
  nodeId: string;
  studentInfo?: StudyInfoType;
  nodeData?: StudyNode[];
  nodeProgress?: ProgressType;
}

const StudyPreview: React.FC<Props> = ({ nodeId, studentInfo, nodeData, nodeProgress }) => {
  const [nodeContentData, setNodeContentData] = useState<NodeContentTypes>();
  const [videoLogParams, setVideoLogParams] = useState<VideoLogParamsType>();
  const [contentData, setContentData] = useState<ContentTypes[]>([]);
  const [exercisesResults, setExercisesResults] = useState<ExercisesResultsType>();
  const [form] = Form.useForm();
  const [rateStudyContent, setRateStudyContent] = useState<number>(0);
  const [rateExerciseTask, setRateExerciseTask] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cacheName, setCacheName] = useState('');
  const [openSubjectiveReview, setOpenSubjectiveReview] = useState(false);
  const [saveSubjectiveReview, setSaveSubjectiveReview] =
    useLocalStorageState<string[]>('modal_subjective_review');

  // 获取习题结果状态
  const { run: getExercisesResultsRequest } = useRequest(
    (nodeId) => {
      return getExercisesResults(nodeId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setExercisesResults(res);
      },
    },
  );

  // 已批阅弹窗
  useEffect(() => {
    if (nodeId && nodeContentData?.studyNode?.reviewState === NodeReviewState.HAVE_REVIEW) {
      if (!saveSubjectiveReview?.includes(nodeId)) {
        setOpenSubjectiveReview(true);
        setSaveSubjectiveReview((prev) => [...(prev || []), nodeId]);
      }
    }
  }, [nodeId, nodeContentData?.studyNode]);

  // 评分数据
  const rateText = ['非常不满意', '不满意', '一般', '满意', '非常满意'];
  useEffect(() => {
    setRateStudyContent(nodeContentData?.studyNode.rateStudyContent || 0);
    setRateExerciseTask(nodeContentData?.studyNode.rateExerciseTask || 0);
  }, [nodeContentData]);

  // 获取节点模块内容
  const { run: getNodeContentRequest } = useRequest(
    (nodeId) => {
      return getNodeContent(nodeId);
    },
    {
      manual: true,
      onSuccess: (res) => {
        setNodeContentData(res);
        // 视频埋点所需参数
        const parames: VideoLogParamsType = {
          batchId: res?.studyNode.batchId,
          nodeId: res?.id,
        };

        setVideoLogParams(parames);
        localStorage.setItem(cacheName, JSON.stringify(res));
      },
    },
  );

  useEffect(() => {
    if (nodeId) {
      setCacheName(`${nodeId}_nodeContentData`);
      getNodeContentRequest(nodeId);
    }
  }, [nodeId]);

  useEffect(() => {
    if (nodeContentData?.studyNode.studyState === NodeStudyState.NODE_SUCCESS) {
      getExercisesResultsRequest(nodeId);
      // 节点信息弹窗
      const getModal = localStorage.getItem(`modal_${nodeId}`);
      if (!getModal) {
        setIsModalOpen(true);
      }
    }
  }, [nodeContentData]);

  useEffect(() => {
    // 提取模块内容
    setContentData(nodeContentData?.contents || []);
    setTimeout(function () {
      nodeContentData?.id && setLoading(false);
    }, 600);
  }, [nodeContentData]);

  // 悬浮栏目导航
  const anchorItems = contentData.map((item, index) => ({
    key: item.id,
    href: `#${item.id}`,
    title: item.name,
  }));

  if (nodeContentData?.nodeDynamic && nodeContentData?.nodeDynamic.content) {
    anchorItems.push(
      {
        key: 'rate_camp',
        href: '#rate_camp',
        title: '打卡心得',
      },
      {
        key: 'rate_study',
        href: '#rate_study',
        title: '学习评价',
      },
    );
  } else {
    anchorItems.push({
      key: 'rate_study',
      href: '#rate_study',
      title: '学习评价',
    });
  }

  // 处理不同板块内容
  const handleContentItem = (item: ContentTypes, index: number): React.ReactNode | null => {
    let result: React.ReactNode | null = null;

    switch (item.type) {
      case NodeContentType.RICH_TEXT:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Editor
              content={editParseJson((item.info as TextType).richText)}
              editable={false}
              titleProps={{ showTitle: false }}
              catalogProps={{ showCatalog: false }}
            />
          );
        }
        break;
      case NodeContentType.EXERCISES:
        if (item.info && typeof item.info === 'object') {
          result = <QuestionPreview item={item} />;
        }
        break;
      case NodeContentType.VIDEO:
        if (item.info && typeof item.info === 'object') {
          result = (
            <Videos
              videoList={item.info as VideoType}
              videoLogParams={videoLogParams}
              contentId={item.id}
            />
          );
        }
        break;
      case NodeContentType.PROFESSIONAL_CERTIFICATION:
        if (item.info) {
          result = <Cert data={item.info as CertificationType} form={form} />;
        }
        break;
      default:
        result = null;
    }

    result = (
      <div style={{ marginBottom: 40 }}>
        <div id={item.id} className="f24 fw600" style={{ marginBottom: 16 }}>
          {item.name}
        </div>
        <Form form={form} key={index} layout="vertical">
          {result}
        </Form>
      </div>
    );

    return result;
  };

  // 下一个节点（当前节点的下一个节点，按顺序）
  const findNextId = () => {
    if (nodeData) {
      const index = nodeData.findIndex((item) => item.id === nodeId);
      if (index !== -1 && index < nodeData.length - 1) {
        return nodeData[index + 1].id;
      }
    }
    return null;
  };

  // 必填滚动
  const errScroll = (nodeQuestionId: string) => {
    const firstErrorElement = document.getElementById(nodeQuestionId + '_title');
    if (firstErrorElement) {
      firstErrorElement.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const nodeScore = (nodeContentData: any) => {
    let nodeScore = nodeContentData?.studyNode.nodeScore || 0;
    let baseScore = nodeContentData?.studyNode.baseScore || 0;
    return nodeScore + baseScore;
  };
  const nodeScoreTips = (nodeContentData: any) => {
    return (
      <div>
        <p>节点基础学分: {nodeContentData?.studyNode.baseScore}</p>
        <p>习题获得的学分: {nodeContentData?.studyNode.nodeScore}</p>
      </div>
    );
  };

  const delNodeModal = () => {
    localStorage.setItem(`modal_${nodeId}`, 'true');
  };

  return (
    <>
      <div>
        {loading && (
          <div style={{ padding: 24 }}>
            <div style={{ width: '90%', marginBottom: 24, padding: '10px 0' }}>
              <Skeleton.Button active block style={{ height: 40 }} />
            </div>

            <div className="flex_0" style={{ width: '90%' }}>
              <div style={{ width: 120 }}>
                <Skeleton.Image active />
              </div>
              <div style={{ flex: 1 }}>
                <Skeleton active loading={loading} paragraph={{ rows: 6 }} />
              </div>
            </div>
          </div>
        )}

        {!loading && (
          <div>
            <div className={cx(styles.top_node_title, 'flex_3')}>
              <div className={styles.node_title}>{nodeContentData?.name}</div>
              {nodeContentData &&
              nodeContentData?.countQuestion &&
              nodeContentData.studyNode?.studyState === NodeStudyState.NODE_SUCCESS ? (
                <div
                  className={cx(
                    styles.top_node_state,
                    nodeContentData.studyNode.reviewState === NodeReviewState.WAIT_REVIEW
                      ? styles.top_node_state_review
                      : exercisesResults?.questionErrorNumber
                      ? styles.top_node_state_err
                      : styles.top_node_state_success,
                  )}
                  onClick={() => {
                    if (
                      exercisesResults &&
                      (nodeContentData.studyNode.reviewState === NodeReviewState.HAVE_REVIEW ||
                        exercisesResults?.questionErrorNumber)
                    ) {
                      errScroll(exercisesResults?.id);
                    }
                  }}
                >
                  {nodeContentData.studyNode.reviewState === NodeReviewState.WAIT_REVIEW ? (
                    <span>助教批阅主观题中</span>
                  ) : exercisesResults?.questionErrorNumber ? (
                    <span>本节有 {exercisesResults.questionErrorNumber} 道错题哦～点我去看看</span>
                  ) : (
                    <span>本节习题全部正确哦～非常厉害</span>
                  )}
                </div>
              ) : (
                ''
              )}
            </div>
            <div className={styles.node_content_main}>
              <div className={styles.node_content}>
                {contentData.map((item, index) => (
                  <div key={index}>{handleContentItem(item, index)}</div>
                ))}

                <div className={styles.new_antd_anchor} style={{ backgroundColor: '#fff' }}>
                  <Anchor items={anchorItems} />
                </div>

                <div className={styles.camp_rate_box}>
                  {nodeContentData?.nodeDynamic && nodeContentData?.nodeDynamic.content && (
                    <div style={{ marginBottom: 40 }}>
                      <div id="rate_camp" className="f24 fw600" style={{ marginBottom: 16 }}>
                        打卡心得
                      </div>
                      {nodeContentData?.nodeDynamic.rewardCredits > 0 && (
                        <div className={cx(styles.reward, 'flex_1')}>
                          <RewardSvg style={{ width: 15, marginTop: 3 }} />
                          <span className="colffa">
                            奖励 {nodeContentData?.nodeDynamic.rewardCredits} 学分
                          </span>
                        </div>
                      )}
                      <Editor
                        key={`editDynamic_${nodeId}`}
                        content={
                          nodeContentData?.nodeDynamic.content &&
                          editParseJson(nodeContentData?.nodeDynamic.content)
                        }
                        titleProps={{ showTitle: false }}
                        catalogProps={{ showCatalog: false }}
                        editable={false}
                      />
                    </div>
                  )}

                  {nodeContentData?.remark !== undefined && nodeContentData?.remark > 0 && (
                    <>
                      <div>
                        <div id="rate_study" className="f24 fw600" style={{ marginBottom: 16 }}>
                          学习评价
                        </div>
                        <div id="rate_study_content" className="flex_1 f14" style={{ gap: 16 }}>
                          <span>【学习内容】方面：</span>
                          <span>
                            <Rate
                              style={{ color: '#FAAD14', fontSize: 26 }}
                              tooltips={rateText}
                              value={rateStudyContent}
                              disabled
                            />
                          </span>
                          <span className="colffa">
                            {rateStudyContent ? (
                              <span>{rateText[rateStudyContent - 1]}</span>
                            ) : null}
                          </span>
                        </div>
                      </div>
                      <div
                        id="rate_exercise_task"
                        className="flex_1 f14"
                        style={{ gap: 16, margin: '16px 0' }}
                      >
                        <span>【习题任务】方面：</span>
                        <span>
                          <Rate
                            style={{ color: '#FAAD14', fontSize: 26 }}
                            tooltips={rateText}
                            value={rateExerciseTask}
                            disabled
                          />
                        </span>
                        <span className="colffa">
                          {rateExerciseTask ? <span>{rateText[rateExerciseTask - 1]}</span> : null}
                        </span>
                      </div>

                      {nodeContentData?.studyNode.rateContent && (
                        <>
                          <div className="f14" style={{ margin: '24px 0 16px' }}>
                            对于今天的课程或者习题你有什么建议或者体验不好的地方嘛
                          </div>
                          <Editor
                            key={`editRate_${nodeId}`}
                            content={editParseJson(nodeContentData?.studyNode.rateContent)}
                            titleProps={{ showTitle: false }}
                            catalogProps={{ showCatalog: false }}
                            editable={false}
                          />
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {!loading && (
          <div className={styles.node_post_main}>
            <div className="flex_1">
              {nodeContentData?.studyNode.studyState == NodeStudyState.NODE_FAIL ? (
                <div>
                  <Button disabled>节点打卡失败</Button>
                </div>
              ) : (
                <>
                  <Button
                    style={{ height: 38, fontSize: 16, marginRight: 18, minWidth: 120 }}
                    type="primary"
                    onClick={() => {
                      findNextId()
                        ? history.push(`?nodeid=${findNextId()}`)
                        : history.push(`/v2/camp/class/${studentInfo?.batchId}`);
                    }}
                  >
                    {findNextId()
                      ? '下一节'
                      : nodeProgress?.allNode === nodeProgress?.successNode
                      ? '已完成所有节点，点我返回'
                      : '返回'}
                  </Button>
                  <div className="felx_1 f16">
                    <Tooltip title={nodeScoreTips(nodeContentData)}>
                      <span className="col5a5">本节获得</span>
                      <span className="colff4 f20" style={{ margin: '0 8px' }}>
                        {nodeScore(nodeContentData)}
                      </span>

                      <span className="col5a5">学分，</span>
                    </Tooltip>
                    <span className="col5a5">当前共</span>
                    <span className="colff4 f20" style={{ margin: '0 8px' }}>
                      {studentInfo?.campScore}
                    </span>
                    <span className="col5a5">学分</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      <Modal
        open={isModalOpen}
        centered
        width={480}
        onCancel={() => {
          setIsModalOpen(false);
          delNodeModal();
        }}
        footer={null}
      >
        <div className={styles.node_modal_box}>
          <div>
            <img src={successSvg} width={200} />
          </div>
          <div className="f18">恭喜您完成 {nodeContentData?.name}</div>

          {nodeContentData?.studyNode.reviewState === NodeReviewState.WAIT_REVIEW ? (
            <div className="colffa f16" style={{ margin: '12px 0' }}>
              本节有 {exercisesResults?.questionReviewNumber} 道主观题需要助教批阅
            </div>
          ) : exercisesResults?.questionErrorNumber ? (
            <span>
              <div className="colffa f16" style={{ margin: '12px 0' }}>
                本节有 {exercisesResults.questionErrorNumber} 道错题哦～
                <span
                  className="col008 hover008"
                  onClick={() => {
                    setIsModalOpen(false);
                    errScroll(exercisesResults?.id);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  点我去看看
                </span>
              </div>
            </span>
          ) : (
            ''
          )}

          <div style={{ width: 210, lineHeight: 2.2 }}>
            <div className="flex_3 col808 f16">
              <span>节点基础学分：</span>
              <span>+{nodeContentData?.studyNode.baseScore}</span>
            </div>
            <div className="flex_3 col808 f16">
              <span>习题获得学分：</span>
              <span>+{nodeContentData?.studyNode.nodeScore}</span>
            </div>
            <div className="flex_3 col808 f16">
              <span>当前总学分：</span>
              <span>{studentInfo?.campScore}</span>
            </div>
          </div>
          <div>
            <div
              className="com_camp_btn f16"
              style={{ height: 40, margin: '24px 0 10px 0', padding: '0 24px', minWidth: 140 }}
              onClick={() => {
                findNextId()
                  ? history.push(`?nodeid=${findNextId()}`)
                  : history.push(`/v2/camp/class/${studentInfo?.batchId}`);
                delNodeModal();
              }}
            >
              {findNextId()
                ? '继续学习下一节'
                : nodeProgress?.allNode === nodeProgress?.successNode
                ? '已完成所有节点，点我返回'
                : '返回'}
            </div>
          </div>
        </div>
      </Modal>

      {/* 已批阅弹窗 */}
      <Modal
        open={openSubjectiveReview}
        centered
        width={480}
        onCancel={() => {
          setOpenSubjectiveReview(false);
        }}
        footer={null}
      >
        <div className={styles.node_modal_box}>
          <div>
            <img src={successSvg} width={200} />
          </div>
          <div className="f18">主观题批阅结果已更新</div>

          <div style={{ width: 210, lineHeight: 2.2, marginTop: 12 }}>
            <div className="flex_3 col808 f16">
              <span>主观题得分：</span>
              <span>+{exercisesResults?.subjectiveScore}</span>
            </div>
            <div className="flex_3 col808 f16">
              <span>当前总学分：</span>
              <span>{studentInfo?.campScore}</span>
            </div>
          </div>
          <div>
            <div
              className="com_camp_btn f16"
              style={{ height: 40, margin: '24px 0 10px 0', padding: '0 24px', width: 200 }}
              onClick={() => {
                setOpenSubjectiveReview(false);
              }}
            >
              去看看
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};
export default StudyPreview;
